<script setup lang="ts">
import { __ } from '@@/bits/intl'
import type { ShareService } from '@@/bits/native_share'
import { buildShareLink, shareLinkOnService } from '@@/bits/native_share'
import { SLIDESHOW_ANALYTICS_ID } from '@@/bits/slideshow'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, { OzMenuRowsFocusStyle, OzMenuRowsSizePreset } from '@@/library/v4/components/OzMenuRows.vue'
import OzScreenSizeAwareSideModal from '@@/library/v4/components/OzScreenSizeAwareSideModal.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWindowSizeStore } from '@@/pinia/window_size'
import SurfacePanelHeader from '@@/vuecomponents/SurfacePanelHeader.vue'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { useSlideshowPanels } from '@@/vuecomposables/slideshow_panels'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const { isSmallerThanTabletPortrait, isSmallerThanDesktop } = storeToRefs(useWindowSizeStore())
const { links, publicKey, title, description, wallId } = storeToRefs(useSurfaceStore())
const { isShareViaAnotherAppPanelOpen } = useSlideshowPanels()
const { coverPageUrl } = useSlideshowPages()
const { hasDarkBackgroundLuminance } = useSlideshowStyles()

interface Group {
  name: string
  rows: OzMenuRowsRow<ShareService>[]
}

const actionListGroups = computed((): Group[] => {
  const group1Rows: OzMenuRowsRow<ShareService>[] = [
    {
      icon: 'mail_outline',
      text: __('Email'),
      eventPayload: 'email',
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-email-button'],
    },
  ]

  const group2Rows: OzMenuRowsRow<ShareService>[] = [
    {
      icon: 'facebook_outline',
      text: __('Facebook'),
      eventPayload: 'facebook',
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-share-on-facebook-button'],
    },
    {
      icon: 'x_logo',
      text: __('Twitter'),
      eventPayload: 'twitter',
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-share-on-twitter-button'],
    },
    {
      icon: 'google_classroom',
      text: __('Google Classroom'),
      eventPayload: 'googleClassroom',
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-share-on-google-classroom-button'],
    },
  ]

  return [
    {
      name: 'group1',
      rows: group1Rows,
    },
    {
      name: 'group2',
      rows: group2Rows,
    },
  ]
})

const getShareLinkFor = (service: ShareService): string => {
  return buildShareLink({
    service,
    ...{
      url: coverPageUrl.value,
      title: service === 'email' ? __('Check out my beautiful slideshow on Padlet') : title.value,
      description: service === 'email' ? coverPageUrl.value : description.value ?? '',
      media: links.value.snap, // TODO: should be replaced by OpenGraph image later
      publicKey: publicKey.value,
      params: '?slideshow=1',
    },
  })
}

const handleAction = async (_event: unknown, service: ShareService) => {
  shareLinkOnService(getShareLinkFor(service), service)
}

const closePanel = () => {
  isShareViaAnotherAppPanelOpen.value = false
}
</script>

<template>
  <OzScreenSizeAwareSideModal
    class="font-sans"
    aria-labelledby="slideshow-share-via-another-app-panel-title"
    :full-size="true"
    :is-phone="isSmallerThanTabletPortrait"
    :width-class="isSmallerThanDesktop ? 'w-100' : 'w-120'"
    :dark-mode="hasDarkBackgroundLuminance"
    :z-index-class="'z-sidepanel'"
    :scrollbar-overlay="true"
    :top-inner-corner-radius="0"
    @scrim-click="closePanel"
    @scrim-esc="closePanel"
  >
    <template #header>
      <SurfacePanelHeader class="pb-3">
        <template #start-content>
          <OzIconButton
            icon-name="chevron_left_center"
            :size-preset="OzIconButtonSizePreset.H32px"
            :label="__('Close slideshow share via another app panel')"
            :color-scheme="OzIconButtonColorScheme.SecondaryClear"
            :dark-mode="hasDarkBackgroundLuminance"
            @click="closePanel"
          />
        </template>
        <template #center-content>
          <h2
            id="slideshow-share-via-another-app-panel-title"
            :class="[
              'text-heading-3 text-center font-semibold',
              hasDarkBackgroundLuminance ? 'text-light-text-100' : 'text-dark-text-100',
            ]"
          >
            {{ __('Sharing') }}
          </h2>
        </template>
      </SurfacePanelHeader>
    </template>
    <template #body>
      <div :class="'px-4 pt-3 pb-5 flex flex-col gap-y-5'">
        <div v-for="(group, i) in actionListGroups" :key="group.name">
          <OzBox :key="i" :radius="20" :color="OzBoxColors.Secondary" :dark-mode="hasDarkBackgroundLuminance">
            <OzMenuRows
              :accessibility-role="'group'"
              :size-preset="OzMenuRowsSizePreset.H48px"
              :focus-style="OzMenuRowsFocusStyle.Bubble100"
              :dark-mode="hasDarkBackgroundLuminance"
              :common-settings="{
                textTruncated: true,
              }"
              :rows="group.rows"
              @select-row="handleAction"
            />
          </OzBox>
        </div>
      </div>
    </template>
  </OzScreenSizeAwareSideModal>
</template>
